import React from "react";
import { useNewClientWizardContext } from "../Context/NewClientWizardContext";
import styles from "../NewClientWizard.module.css";
import { ProductConfig } from "../types";

const STORAGE_TYPES = [
  { id: 1, label: "Blob Storage (Azure)" },
  { id: 2, label: "File System" },
  { id: 3, label: "Blob Storage (Local)" },
];

/** Step 1 — database, storage, and incoming-handler configuration for the product
 * being provisioned. Mirrors the fields of the Client-Products Configuration tab. */
export function DatabaseConfigStep({ productName }: { productName: string }) {
  const { formData, updateFormData } = useNewClientWizardContext();
  const config = formData.config;

  const updateConfig = (changes: Partial<ProductConfig>) => {
    updateFormData({ config: { ...config, ...changes } });
  };

  return (
    <div className={styles.stepBody}>
      <div className={styles.productConfigBlock}>
        <h4>{productName}</h4>

        <h5>Database (will be created by provisioning)</h5>
        <div className={styles.fieldRow}>
          <label>Database Server *</label>
          <input
            type="text"
            value={config.databaseServer}
            onChange={(e) => updateConfig({ databaseServer: e.target.value })}
            placeholder="e.g. sqldb-pbin-dev.database.windows.net"
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Database Name *</label>
          <input
            type="text"
            value={config.databaseCatalog}
            onChange={(e) => updateConfig({ databaseCatalog: e.target.value })}
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Elastic Pool</label>
          <input
            type="text"
            value={config.elasticPoolName}
            onChange={(e) => updateConfig({ elasticPoolName: e.target.value })}
            placeholder="Azure SQL elastic pool name - blank for standalone/VM"
          />
        </div>
        <p className={styles.hint}>
          On Azure SQL (dev/test), name the elastic pool the new database should join —
          without it the database is created standalone at the server's default tier.
          Leave blank for VM-hosted SQL Server and local machines.
        </p>
        <div className={styles.fieldRow}>
          <label>Database User</label>
          <input
            type="text"
            value={config.databaseUser}
            onChange={(e) => updateConfig({ databaseUser: e.target.value })}
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Database Password</label>
          <input
            type="password"
            value={config.databasePassword}
            onChange={(e) => updateConfig({ databasePassword: e.target.value })}
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Provisioning SQL User</label>
          <input
            type="text"
            value={config.provisioningSqlUser}
            onChange={(e) => updateConfig({ provisioningSqlUser: e.target.value })}
            placeholder="dbcreator login - used once, never stored"
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Provisioning SQL Password</label>
          <input
            type="password"
            value={config.provisioningSqlPassword}
            onChange={(e) => updateConfig({ provisioningSqlPassword: e.target.value })}
          />
        </div>
        <p className={styles.hint}>
          The provisioning credential creates the database on the server above. It is
          relayed to PBIN for this run only and never stored. Leave both fields blank on a
          local machine to use integrated security.
        </p>
        <div className={styles.fieldRow}>
          <label>GAB Connectivity</label>
          <select
            value={config.gabFlavor}
            onChange={(e) => updateConfig({ gabFlavor: e.target.value as "AzureSql" | "LocalStub" | "SqlServerVm" })}
          >
            <option value="AzureSql">Azure SQL (elastic query to shared GAB)</option>
            <option value="SqlServerVm">SQL Server VM (same-instance cross-database views)</option>
            <option value="LocalStub">Local stub (dev/test on plain SQL Server only)</option>
          </select>
        </div>
        <p className={styles.hint}>
          Azure SQL for Azure environments (dev/test); SQL Server VM for VM-hosted
          environments (production) where the GAB is a database on the same instance.
          Local stub creates the GAB_* objects as empty local tables — only for developer
          machines without GAB access.
        </p>

        <h5>Auth Database (existing, shared)</h5>
        <p className={styles.hint}>
          Required — user assignment resolves local user accounts through the shared Auth
          database; without it, users cannot be added to the product. Leave user/password
          blank on a local machine to use integrated security.
        </p>
        <div className={styles.fieldRow}>
          <label>Auth Server *</label>
          <input
            type="text"
            value={config.authDatabaseServer}
            onChange={(e) => updateConfig({ authDatabaseServer: e.target.value })}
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Auth Catalog *</label>
          <input
            type="text"
            value={config.authDatabaseCatalog}
            onChange={(e) => updateConfig({ authDatabaseCatalog: e.target.value })}
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Auth User</label>
          <input
            type="text"
            value={config.authDatabaseUser}
            onChange={(e) => updateConfig({ authDatabaseUser: e.target.value })}
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Auth Password</label>
          <input
            type="password"
            value={config.authDatabasePassword}
            onChange={(e) => updateConfig({ authDatabasePassword: e.target.value })}
          />
        </div>

        <h5>Storage</h5>
        <div className={styles.fieldRow}>
          <label>Storage Type *</label>
          <select
            value={config.storageTypeId}
            onChange={(e) => updateConfig({ storageTypeId: Number(e.target.value) })}
          >
            {STORAGE_TYPES.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className={styles.fieldRow}>
          <label>Storage Path</label>
          <input
            type="text"
            value={config.storagePath}
            onChange={(e) => updateConfig({ storagePath: e.target.value })}
            placeholder="e.g. dbsso-pbins"
          />
        </div>

        <h5>Incoming Handler (optional)</h5>
        <div className={styles.fieldRow}>
          <label>Email Server</label>
          <input
            type="text"
            value={config.incomingHandlerEmailServer}
            onChange={(e) => updateConfig({ incomingHandlerEmailServer: e.target.value })}
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Email User</label>
          <input
            type="text"
            value={config.incomingHandlerEmailUser}
            onChange={(e) => updateConfig({ incomingHandlerEmailUser: e.target.value })}
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Email Password</label>
          <input
            type="password"
            value={config.incomingHandlerEmailPassword}
            onChange={(e) => updateConfig({ incomingHandlerEmailPassword: e.target.value })}
          />
        </div>
        <div className={styles.fieldRow}>
          <label>Connection Port</label>
          <input
            type="text"
            value={config.incomingHandlerConnectionPort}
            onChange={(e) => updateConfig({ incomingHandlerConnectionPort: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}
